import i18next from "i18next";
import { ActionsType, ColumnsType } from "src/interfaces/Common";

const CONFIG_LABELS_NAMESPACE = "config-labels";

type ConfigLabelOverrides = Record<string, unknown>;

const isOverridesNode = (value: unknown): value is ConfigLabelOverrides =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getConfigLabelOverrides = (
  lang: string,
  pageId?: string,
): ConfigLabelOverrides | undefined => {
  if (!pageId) return undefined;
  const bundle = i18next.getResourceBundle(lang, CONFIG_LABELS_NAMESPACE);
  const overrides = bundle?.[pageId];
  return isOverridesNode(overrides) ? overrides : undefined;
};

const getLabelOverride = (
  overrides: ConfigLabelOverrides | undefined,
  key: string,
): string | undefined => {
  const override = overrides?.[key];
  return typeof override === "string" ? override : undefined;
};

export const getConfigLabelOverride = (
  lang: string,
  pageId: string | undefined,
  key: string,
): string | undefined =>
  getLabelOverride(getConfigLabelOverrides(lang, pageId), key);

const translateFields = (
  fields: any[] | undefined,
  overrides: ConfigLabelOverrides | undefined,
  columnKey: string,
) => {
  if (!fields) return fields;
  const fieldOverrides = overrides?.[columnKey];
  if (!isOverridesNode(fieldOverrides)) return fields;
  return fields.map((field) => ({
    ...field,
    label: getLabelOverride(fieldOverrides, field.accessorKey) ?? field.label,
  }));
};

export const translateHeaders = (
  columns: ColumnsType[],
  lang: string,
  pageId?: string,
): ColumnsType[] => {
  const overrides = getConfigLabelOverrides(lang, pageId);
  const translatedColumns = columns?.map((col) => {
    const fields = translateFields(
      (col as any)?.fields,
      overrides,
      col.accessorKey,
    );
    return {
      ...col,
      ...(fields ? { fields } : {}),
      header:
        getLabelOverride(overrides, col.accessorKey) ??
        (col?.labelHeader ? col?.labelHeader[lang] : col.header),
      form_box: col?.form_box
        ? col?.form_box?.map((colFormBox: ColumnsType) => {
            return {
              ...colFormBox,
              header:
                getLabelOverride(overrides, colFormBox.accessorKey) ??
                (colFormBox?.labelHeader
                  ? colFormBox?.labelHeader[lang]
                  : colFormBox.header),
            };
          })
        : [],
    };
  });
  return translatedColumns;
};

export const translateActions = (
  actions: ActionsType[],
  lang: string,
): ActionsType[] => {
  return actions?.map((action) => {
    const { actions = [] } = action;
    return {
      ...action,
      labelTranslate: action.label[lang],
      actions: actions.map((internalAction) => ({
        ...internalAction,
        labelTranslate: internalAction.label[lang],
      })),
    };
  });
};
